/**
 * HubSpot service.
 * This file contains functions that will make HubSpot integration easier.
 */

import { getLocalStorage } from '../localStorageHandler';
import { getEverflowAffiliateId, getRewardfulAffiliateId } from '../propagateQueryParams';
import { trackInteraction } from '../analytics/trackInteraction';

export type OnFormSubmitType = (form: HTMLFormElement, submissionValues: SubmissionValuesData) => void;

export type OnBeforeFormSubmitType = (form: HTMLFormElement, submissionValues: SubmissionValuesData) => void;

export type OnFormSubmittedType = (
  form: HTMLFormElement,
  data: {
    redirectUrl: string;
    submissionValues: SubmissionValuesData;
  }
) => void;

export type OnFormReadyType = (form: HTMLFormElement) => void;

export interface AnnualRevenue {
  '0-2/annual_revenue_range'?: string;
  '0-2/annual_revenue_range_v2'?: string;
  annual_revenue_range_v2?: string;
}

export interface SubmissionValuesData extends AnnualRevenue {
  '0-2/top_revenue_driver'?: string;
  '0-2/launch_timeframe'?: string;
  '0-2/what_s_your_top_reason_to_explore_mighty_pro_'?: string[];
  'LEGAL_CONSENT.subscription_type_81053228'?: boolean;
  hs_context?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  role?: string;
}

export interface CreateHubSpotFormProps {
  formContainer: string;
  isSandbox?: boolean;
  formId: string;
  onFormSubmitCallback?: OnFormSubmitType;
  onBeforeFormSubmitCallback?: OnBeforeFormSubmitType;
  onFormSubmittedCallback?: OnFormSubmittedType;
  onFormReadyCallback?: OnFormReadyType;
  successCallback?: () => void;
  errorCallback?: () => void;
}

export interface HubSpotMessageEvent {
  meetingBookSucceeded?: boolean;
  meetingsPayload?: any;
  eventName?: 'onFormSubmitted' | 'onBeforeFormSubmit' | 'onFormSubmit';
  data: any;
  conversionId?: string;
  accepted?: boolean;
}

export interface HubSpotOnBeforeSubmitData {
  data: {
    name: 'firstname' | 'lastname' | 'email' | '0-2/annual_revenue_range_v2';
    value: string;
  }[];
}

export interface CreateHubSpotCalendarForm {
  calendarContainer: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface HubSpotApiData {
  forms?: {
    create: (obj: {
      portalId: string;
      formId: string;
      target: string;
      onBeforeFormSubmit?: OnBeforeFormSubmitType;
      onFormSubmit?: OnFormSubmitType;
      onFormSubmitted?: OnFormSubmittedType;
      onFormReady?: OnFormReadyType;
    }) => void;
  };
  createMeetingsIframe?: (container: string) => void;
}

/**
 * Check if value is valid annual revenue for P2P, that means the annual revenue should be under 250.
 */
export const annualRevenueValuesUnder250 = ['Under $250K', 'Under $250,000'];
export type HubSpotRevenueValue = (typeof annualRevenueValuesUnder250)[number];

export const isAnnualRevenueUnder250 = (annualRevenue: HubSpotRevenueValue): annualRevenue is HubSpotRevenueValue =>
  annualRevenueValuesUnder250.includes(annualRevenue);

export const isQualifiedProLead = (annualRevenue: HubSpotRevenueValue): boolean => {
  return !isAnnualRevenueUnder250(annualRevenue);
};

/**
 * Return value of Annual Revenue
 * @param data SubmissionValuesData
 */
export const getAnnualRevenueFromData = (data: SubmissionValuesData): string => {
  const referenceObj: AnnualRevenue = {
    '0-2/annual_revenue_range': null,
    '0-2/annual_revenue_range_v2': null,
    annual_revenue_range_v2: null,
  };

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (key in referenceObj || key.includes('annual_revenue')) {
        return data[key as keyof AnnualRevenue];
      }
    }
  }

  return '';
};

export const getTopRevenueDriverFromData = (data: SubmissionValuesData): string => {
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (key === '0-2/top_revenue_driver') {
        return data[key as keyof SubmissionValuesData] as string;
      }
    }
  }

  return '';
};

/**
 * Wait for HubSpot dependencies to be initialized
 * @param keyToBeWaited key that we are going to listen to.
 * @returns void
 */
const waitForHubSpot = async (keyToBeWaited: string, requiredFunctions?: string[]): Promise<HubSpotApiData> => {
  if (typeof window === 'undefined') return;

  return new Promise((resolve, reject) => {
    let tries = 0;
    const intervalId = setInterval(() => {
      if ((window as any)[keyToBeWaited]) {
        // Check for functions that are required to be loaded.
        if (requiredFunctions?.length) {
          let functionNotFound = false;

          requiredFunctions.forEach((requiredFunction) => {
            if (!(window as any)[keyToBeWaited][requiredFunction]) {
              functionNotFound = true;
            }
          });

          if (functionNotFound) return;
        }

        resolve((window as any)[keyToBeWaited]);
        clearInterval(intervalId);
      } else if (++tries > 50) {
        reject(new Error(`${keyToBeWaited} not initialized after time limit`));
      }
    }, 200); // 10 Seconds in total, 1 try each 200ms.
  });
};

/**
 * Listen to window.message context.
 * @param callback function to be executed when listening.
 * @returns void
 */
export const addHubSpotMessageListener = <T = MessageEvent>(callback: (e?: MessageEvent<T>) => void): void => {
  if (typeof window === 'undefined') return;

  window.addEventListener('message', callback);
};

/**
 * Remove function from listening window.message context.
 * @param callback function to be removed from listening.
 * @returns void
 */
export const removeHubSpotMessageListener = <T>(callback: (e?: MessageEvent<T>) => void): void => {
  if (typeof window === 'undefined') return;

  window.removeEventListener('message', callback);
};

/**
 * Create HubSpot form with formId based on env.
 * @param createCallback callback called after form creation
 * @returns void
 */
export const createHubSpotForm = ({
  formContainer,
  formId,
  onFormSubmitCallback,
  onBeforeFormSubmitCallback,
  onFormSubmittedCallback,
  onFormReadyCallback,
  successCallback = () => null,
  errorCallback = () => null,
}: CreateHubSpotFormProps): void => {
  if (typeof window === 'undefined') return;

  const portalId = process.env.GATSBY_HUBSPOT_FORM_PORTAL_ID || '24271906';

  const alteredFormReadyCallback = (form: HTMLFormElement) => {
    if (onFormReadyCallback) {
      onFormReadyCallback(form);
    }
  };

  const alteredFormSubmittedCallback: OnFormSubmittedType = (form, data) => {
    trackInteraction(`form-${formId}`, 'hubspot-form', 'form_submit', form);

    if (onFormSubmittedCallback) {
      onFormSubmittedCallback(form, data);
    }
  };

  waitForHubSpot('hbspt', ['forms', 'meetings'])
    .then((hubSpot) => {
      hubSpot.forms?.create({
        portalId: portalId,
        formId: formId,
        target: formContainer,
        onFormSubmit: onFormSubmitCallback,
        onBeforeFormSubmit: onBeforeFormSubmitCallback,
        onFormSubmitted: alteredFormSubmittedCallback,
        onFormReady: alteredFormReadyCallback,
      });

      successCallback();
    })
    .catch((error) => {
      errorCallback();

      (window as any).TrackJS && (window as any).TrackJS.track(`HubSpot Form Not loaded: ${error?.message}`);
    });
};

/**
 * Render HubSpot calendar iframe into a container
 * @param containerClass class name of the container which the iframe will be rendered.
 * @returns
 */
export const createHubSpotCalendarForm = ({
  calendarContainer,
  firstName = '',
  lastName = '',
  email = '',
}: CreateHubSpotCalendarForm): void => {
  if (typeof window === 'undefined') return;
  if (!document.querySelector(calendarContainer)) {
    (window as any).TrackJS && (window as any).TrackJS.console.error('Calendar form container missing!');

    return;
  }

  // Prevent multiple renders
  if (document.querySelector(`${calendarContainer} iframe`)) {
    return;
  }

  waitForHubSpot('MeetingsEmbedCode', ['createMeetingsIframe'])
    .then((MeetingsEmbedCode) => {
      const isPathToProCalendar = getLocalStorage('requestADemo:pathToProCalendar');
      const pathToProCalendarUrl =
        process.env.GATSBY_HUBSPOT_PATH_TO_PRO_CALENDAR_URL ||
        'https://explore.mightynetworks.com/meetings/mighty-pro-team/intro-to-mighty-pro-path-to-pro?embed=true';
      const mightyProCalendarUrl =
        process.env.GATSBY_HUBSPOT_MIGHTY_PRO_CALENDAR_URL ||
        'https://explore.mightynetworks.com/meetings/mighty-pro-team/mighty-pro-demo?embed=true';
      const calendarUrl = isPathToProCalendar === 'true' ? pathToProCalendarUrl : mightyProCalendarUrl;

      const calendarDiv = document.createElement('div');
      calendarDiv.className = 'meetings-iframe-container';
      calendarDiv.setAttribute('data-src', `${calendarUrl}&firstName=${firstName}&lastName=${lastName}&email=${email}`);

      document.querySelector(calendarContainer).appendChild(calendarDiv);

      MeetingsEmbedCode.createMeetingsIframe('.meetings-iframe-container');
    })
    .catch((error) => {
      (window as any).TrackJS && (window as any).TrackJS.track(`HubSpot Calendar Not loaded: ${error?.message}`);
    });
};

/**
 * Submit form based on querySelector of the hubportForm wrapper ID.
 * @returns
 */
export const submitHubSpotFormBuilder = (formSelectorId: string) => (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const formSubmitButton: HTMLButtonElement = document.querySelector(`${formSelectorId} form input[type="submit"]`);

  if (!formSubmitButton) {
    (window as any).TrackJS && (window as any).TrackJS.track('Request a Call submit button not found!');

    return;
  }

  formSubmitButton.click();
};

/**
 * Add listener for form
 * @param formId string
 * @param callback function
 * @returns void
 */
export const addTrackHubSpotFormHandling = (formId: string, callback: (e?: Event) => void): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  document.getElementById(formId)?.addEventListener('click', callback);
  document.querySelector(`#${formId} form input`)?.addEventListener('focus', callback);
};

/**
 * Remove listener for form
 * @param formId string
 * @param callback function
 * @returns void
 */
export const removeTrackHubSpotFormHandling = (formId: string, callback: (e?: Event) => void): void => {
  if (typeof window === 'undefined' && typeof document === 'undefined') return;

  document.getElementById(formId)?.removeEventListener('click', callback);
  document.querySelector(`#${formId} form input`)?.removeEventListener('focus', callback);
};

/**
 * Prefill RewardfulId (?via) param to the HubSpot form
 * @param formId string
 * @returns void
 */
export const prefillRewardfulAffiliateId = (formId: string): void => {
  const affiliateId = getRewardfulAffiliateId() || getEverflowAffiliateId();
  const affiliateIdInput = document.querySelector<HTMLInputElement>(`${formId} input[name="0-2/affiliate_id"]`);

  if (!affiliateIdInput || !affiliateId) return;

  affiliateIdInput.value = affiliateId;
};

/**
 * Prefill Email in hidden HubSpot form
 * @param email string
 * @param formId string
 * @returns void
 */
export const prefillEmail = (email: string, formId: string): void => {
  const emailInput = document.querySelector<HTMLInputElement>(`${formId} input[name="email"]`);

  if (!emailInput || !email) return;

  emailInput.value = email;
};

/**
 * Prefill any field in a HubSpot form
 * @param formId string. Pass the formId only, the function will fill the query.
 * @param fieldQuery string
 * @param data any
 */
export const prefillField = (formId: string, fieldQuery: string, data: string): void => {
  const field = document.querySelector<HTMLInputElement>(`#${formId} ${fieldQuery}`);

  if (!field || !data) return;

  field.value = data;
};
